import { useEffect, useRef, useState } from "react"
import { Clock } from "lucide-react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { Badge } from "@/components/ui/badge"
import { dueWarnings, formatClock, lessonClockState, type LessonClockWarning } from "@/lib/lessonClock"

// Time left in the lesson, in the classroom header (#711). Counts down to the
// booked end, then up as overtime, and says so once, out loud, five minutes
// before the end and ten minutes before Daily closes the room: that ejection
// used to land on both people mid-sentence with no warning at all.
// Renders nothing when the join answer carried no end time.
export function LessonClock({ endsAt }: { endsAt: string | null }) {
  const { t } = useTranslation()
  const endsAtMs = endsAt ? Date.parse(endsAt) : NaN
  const valid = Number.isFinite(endsAtMs)
  const [now, setNow] = useState(() => Date.now())
  const warned = useRef<Set<LessonClockWarning>>(new Set())

  useEffect(() => {
    if (!valid) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [valid])

  useEffect(() => {
    if (!valid) return
    for (const warning of dueWarnings(endsAtMs, now)) {
      if (warned.current.has(warning)) continue
      warned.current.add(warning)
      toast.warning(t(warning === "endingSoon" ? "liveSession.clock.endingSoon" : "liveSession.clock.roomClosing"))
    }
  }, [valid, endsAtMs, now, t])

  if (!valid) return null

  const { phase, ms } = lessonClockState(endsAtMs, now)
  const time = formatClock(ms, phase)
  return (
    <Badge
      variant={phase === "overtime" ? "destructive" : "outline"}
      className="gap-1 tabular-nums"
      data-testid="lesson-clock"
      data-phase={phase}
    >
      <Clock className="w-3 h-3" />
      {t(phase === "running" ? "liveSession.clock.remaining" : "liveSession.clock.overtime", { time })}
    </Badge>
  )
}
